import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useSandboxToken } from '../hooks/useSandboxToken';
import type { StorePreferences } from '@/types';

declare global {
  interface Window {
    ChatWidget?: {
      init: (options: {
        storeId: string;
        sandboxToken?: string;
        containerElement: HTMLElement | null;
        previewConfig?: Partial<StorePreferences>;
        inline?: boolean;
        apiUrl?: string;
      }) => void;
      updateConfig?: (newConfig: Partial<StorePreferences>) => void;
      unmount?: () => void;
    };
  }
}

interface WidgetPreviewProps {
  preferences: StorePreferences;
}

export const WidgetPreview: React.FC<WidgetPreviewProps> = ({ preferences }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: sandboxToken, isLoading } = useSandboxToken(preferences.storeId);

  const preferencesRef = useRef(preferences);
  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  // Event-driven widget initialization & clean unmount lifecycle
  useEffect(() => {
    if (!preferences.storeId || !sandboxToken) return;

    const handleReady = () => {
      if (window.ChatWidget && containerRef.current) {
        const currentPrefs = preferencesRef.current;
        window.ChatWidget.init({
          storeId: currentPrefs.storeId,
          sandboxToken: sandboxToken,
          containerElement: containerRef.current,
          apiUrl: import.meta.env.VITE_API_URL,
          previewConfig: {
            widgetTitle: currentPrefs.widgetTitle,
            agentName: currentPrefs.agentName,
            widgetPrimaryColor: currentPrefs.widgetPrimaryColor,
            widgetSecondaryColor: currentPrefs.widgetSecondaryColor,
            greetingMessage: currentPrefs.greetingMessage,
            isWidgetEnabled: true, // Always enable in administrative sandbox preview
          },
          // Note: Omitting inline: true so the widget renders as a native floating fab/chat window.
        });
      }
    };

    // If widget script is already loaded and ready
    if (window.ChatWidget) {
      handleReady();
    } else {
      // Listen for ready events dispatched by the widget SDK
      window.addEventListener('chatwidget:ready', handleReady, { once: true });
      window.addEventListener('chatagent:ready', handleReady, { once: true });
    }

    // Inject the script if not already present
    const existingScript = document.getElementById('chatagent-widget-script') as HTMLScriptElement | null;
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'chatagent-widget-script';
      const rawWidgetUrl = import.meta.env.VITE_WIDGET_URL;
      let widgetSrc: string;

      if (rawWidgetUrl) {
        if (/\.js($|\?)/.test(rawWidgetUrl)) {
          widgetSrc = rawWidgetUrl;
        } else {
          widgetSrc = `${rawWidgetUrl.replace(/\/+$/, '')}/widget.js`;
        }
      } else {
        widgetSrc = `${window.location.protocol}//${window.location.hostname}:5174/widget.js`;
      }

      script.src = widgetSrc;
      script.async = true;
      script.onload = handleReady;
      document.body.appendChild(script);
    }

    return () => {
      window.removeEventListener('chatwidget:ready', handleReady);
      window.removeEventListener('chatagent:ready', handleReady);
      window.ChatWidget?.unmount?.();
    };
  }, [preferences.storeId, sandboxToken]);

  // Watch for real-time config changes and push them to the widget
  useEffect(() => {
    if (window.ChatWidget?.updateConfig) {
      window.ChatWidget.updateConfig({
        widgetTitle: preferences.widgetTitle,
        agentName: preferences.agentName,
        widgetPrimaryColor: preferences.widgetPrimaryColor,
        widgetSecondaryColor: preferences.widgetSecondaryColor,
        greetingMessage: preferences.greetingMessage,
      });
    }
  }, [
    preferences.widgetTitle,
    preferences.agentName,
    preferences.widgetPrimaryColor,
    preferences.widgetSecondaryColor,
    preferences.greetingMessage,
  ]);

  return (
    <>
      {isLoading && (
        <div className="fixed bottom-6 right-6 z-[9999] p-3 px-4 bg-background border border-border shadow-lg rounded-full flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span>Carregando teste do chat...</span>
        </div>
      )}
      {/* Zero-size mount point for the widget; the widget will inject its own fixed positioning */}
      <div ref={containerRef} id="chat-preview-mount" className="fixed z-[9999]" />
    </>
  );
};
